import {
  useLayoutEffect,
  useState,
  type CSSProperties,
  type RefObject,
} from "react";
import {
  computeInputPopoverPlacement,
  type InputPopoverAlign,
  type InputPopoverRect,
  type InputPopoverSide,
} from "./input-popover-placement";

type PopoverStrategy = "absolute" | "fixed";

interface InputPopoverPlacementOptions {
  active?: boolean;
  align?: InputPopoverAlign;
  anchorRef?: RefObject<HTMLElement | null>;
  gap?: number;
  getAnchor?: () => HTMLElement | null;
  preferredSide?: InputPopoverSide;
  strategy?: PopoverStrategy;
}

const HIDDEN_STYLE: CSSProperties = {
  left: 0,
  position: "absolute",
  top: 0,
  visibility: "hidden",
};

function rectFromDom(rect: DOMRect): InputPopoverRect {
  return {
    bottom: rect.bottom,
    height: rect.height,
    left: rect.left,
    right: rect.right,
    top: rect.top,
    width: rect.width,
  };
}

function viewportFor(
  anchor: HTMLElement,
  strategy: PopoverStrategy,
): Omit<InputPopoverRect, "height" | "width"> {
  if (strategy === "absolute") {
    const pane = anchor.closest("[data-canvas-viewport]") as HTMLElement | null;
    if (pane) return rectFromDom(pane.getBoundingClientRect());
  }
  return {
    bottom: window.innerHeight,
    left: 0,
    right: window.innerWidth,
    top: 0,
  };
}

function resolveDefaultAnchor(el: HTMLElement): HTMLElement | null {
  return (
    (el.closest("[data-input-bar-root]") as HTMLElement | null) ??
    (el.offsetParent as HTMLElement | null) ??
    el.parentElement
  );
}

export function useInputPopoverPlacement(
  popoverRef: RefObject<HTMLElement | null>,
  {
    active = true,
    align = "start",
    anchorRef,
    gap,
    getAnchor,
    preferredSide = "above",
    strategy = "absolute",
  }: InputPopoverPlacementOptions = {},
): CSSProperties {
  const [style, setStyle] = useState<CSSProperties>(HIDDEN_STYLE);

  useLayoutEffect(() => {
    if (!active) {
      setStyle(HIDDEN_STYLE);
      return;
    }

    const el = popoverRef.current;
    const anchor =
      getAnchor?.() ??
      anchorRef?.current ??
      (el ? resolveDefaultAnchor(el) : null);
    if (!el || !anchor) {
      setStyle(HIDDEN_STYLE);
      return;
    }

    const measure = () => {
      const anchorRect = rectFromDom(anchor.getBoundingClientRect());
      const popoverRect = rectFromDom(el.getBoundingClientRect());
      const viewport = viewportFor(anchor, strategy);
      const placement = computeInputPopoverPlacement({
        align,
        anchor: anchorRect,
        gap,
        popover: popoverRect,
        preferredSide,
        viewport,
      });
      const offsetRect =
        strategy === "fixed"
          ? { left: 0, top: 0 }
          : rectFromDom(
              (
                (el.offsetParent as HTMLElement | null) ?? anchor
              ).getBoundingClientRect(),
            );
      const nextStyle: CSSProperties = {
        left: placement.left - offsetRect.left,
        maxHeight: placement.maxHeight,
        maxWidth: placement.maxWidth,
        position: strategy,
        top: placement.top - offsetRect.top,
        visibility: "visible",
      };
      if (placement.width !== undefined) nextStyle.width = placement.width;

      setStyle((prev) =>
        prev.left === nextStyle.left &&
        prev.maxHeight === nextStyle.maxHeight &&
        prev.maxWidth === nextStyle.maxWidth &&
        prev.position === nextStyle.position &&
        prev.top === nextStyle.top &&
        prev.visibility === nextStyle.visibility &&
        prev.width === nextStyle.width
          ? prev
          : nextStyle,
      );
    };

    measure();
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(measure);
    observer?.observe(el);
    observer?.observe(anchor);
    const pane = anchor.closest("[data-canvas-viewport]") as HTMLElement | null;
    if (pane) observer?.observe(pane);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    let frameId = 0;
    const measureFrame = () => {
      measure();
      frameId = requestAnimationFrame(measureFrame);
    };
    frameId = requestAnimationFrame(measureFrame);
    return () => {
      cancelAnimationFrame(frameId);
      observer?.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [
    active,
    align,
    anchorRef,
    gap,
    getAnchor,
    popoverRef,
    preferredSide,
    strategy,
  ]);

  return style;
}
